import type { ItineraryDay, PublicItineraryNode, PublicTimeSlot } from "../types";

type NarrativeNode = Pick<PublicItineraryNode, "nodeType" | "timeSlot" | "sortOrder" | "title" | "description">;
type NarrativeDay = Pick<ItineraryDay, "morningSummary" | "afternoonSummary" | "eveningSummary" | "summary" | "legacyCompatibility"> & {
  nodes: readonly NarrativeNode[];
};
const slotOrder: PublicTimeSlot[] = ["morning", "afternoon", "evening", "unscheduled"];

/** 将历史富文本换行还原为编辑态文本。 */
export function editableSummary(value?: string | null) {
  return (value ?? "").replace(/<br\s*\/?\s*>/gi, "\n");
}

/** 将编辑态换行按原有契约保存为硬换行。 */
export function storedSummary(value: string) {
  return value.replace(/\r\n?/g, "\n").replace(/\n/g, "<br>");
}

/** 空白、空段落和不换行空格不构成客户可见正文。 */
export function hasTextContent(value?: string | null) {
  return Boolean(editableSummary(value).replace(/<[^>]*>|&nbsp;|&#160;|&#xA0;/gi, "").trim());
}

/** 历史正文按节点顺序追加尚未包含的介绍，独立成稿不再追加素材。 */
export function composePointNarrative(
  content: string | null | undefined,
  nodes: readonly NarrativeNode[],
  composed = false,
): { content: string; pointTitle?: string }[] {
  const text = editableSummary(content);
  const result: { content: string; pointTitle?: string }[] = [];
  if (hasTextContent(text)) result.push({ content: text.trim() });
  if (composed) return result;
  for (const node of nodes) {
    const description = node.description?.trim();
    if (node.nodeType === "point" && description && hasTextContent(description) && !text.includes(description)) {
      result.push({ content: description, pointTitle: node.title });
    }
  }
  return result;
}

/** 首次全天接管与客户输出共用原正文顺序，避免关闭兼容时隐藏旧学习材料。 */
export function buildInheritedDayNarratives(day: NarrativeDay, nodes = day.nodes) {
  const content = { morning: day.morningSummary, afternoon: day.afternoonSummary, evening: day.eveningSummary, unscheduled: null };
  const ordered = [...nodes].sort((a, b) => slotOrder.indexOf(a.timeSlot) - slotOrder.indexOf(b.timeSlot) || a.sortOrder - b.sortOrder);
  const composed = day.legacyCompatibility === false;
  if (!Object.values(content).some(hasTextContent) && hasTextContent(day.summary)) {
    return [{ slot: "unscheduled" as PublicTimeSlot, paragraphs: composePointNarrative(day.summary, ordered, composed) }];
  }
  return slotOrder.map((slot) => ({ slot,
    paragraphs: composePointNarrative(content[slot], ordered.filter((node) => node.timeSlot === slot), composed),
  })).filter((part) => part.paragraphs.length > 0);
}
